import {
  Button,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@design-system';
import { ConditionEditor, useSubsectionSettings } from '@/features/quote-and-apply-builder';

interface SubsectionSettingsDialogProps {
  open: boolean;
  subsection: PolicyFormSubSection;
  questions: Record<string, PolicyFormQuestion>;
  onSave: (updates: Partial<PolicyFormSubSection>) => void;
  onClose: () => void;
}

export function SubsectionSettingsDialog({
  open,
  subsection,
  questions,
  onSave,
  onClose,
}: SubsectionSettingsDialogProps) {
  const {
    title,
    setTitle,
    hasCondition,
    setHasCondition,
    condition,
    setCondition,
    handleSave,
  } = useSubsectionSettings({ subsection, questions, onSave, onClose });

  return (
    <Dialog
      open={open}
      onOpenChange={onClose}
    >
      <DialogContent className="max-h-[95vh] gap-6 overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Subsection Settings</DialogTitle>
        </DialogHeader>
        <div className="space-y-6">
          <div className="space-y-2">
            <Label htmlFor="subsection-title">Subsection Title</Label>
            <Input
              value={title}
              onChange={event => setTitle(event.target.value)}
              placeholder="Enter subsection title"
              id="subsection-title"
            />
          </div>

          <div className="space-y-6">
            <div className="space-y-2">
              <Label htmlFor="show-hide-rule">Show/Hide Rule</Label>
              <Select
                value={hasCondition ? 'yes' : 'no'}
                onValueChange={value => setHasCondition(value === 'yes')}
              >
                <SelectTrigger
                  id="show-hide-rule"
                  className="w-full"
                >
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="no">Always Show</SelectItem>
                  <SelectItem value="yes">Conditional</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {hasCondition && (
              <div className="space-y-3 rounded-lg border p-4">
                <p className="text-sm text-gray-500">
                  Define when this subsection should be visible.
                </p>
                <ConditionEditor
                  condition={condition}
                  questions={questions}
                  onChange={setCondition}
                />
              </div>
            )}
          </div>
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={onClose}
          >
            Cancel
          </Button>
          <Button onClick={handleSave}>Save</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
